import React, {FC, useEffect, useState} from 'react'
import {format, parseISO} from 'date-fns'
import Swal from 'sweetalert2'
import API_CONFIG from '../../../../config/api'
import {publicApiKeysApi, PublicApiKey, PublicApiScope} from '../services/publicApiKeysApi'
import {Toast} from '../../../../_metronic/partials/widgets'
import {EmptyState} from '../../shared/components/EmptyState'
import CreateApiTokenModal from './CreateApiTokenModal'
import {escapeHtml} from '../../../../_metronic/helpers'

const SCOPE_LABELS: Record<PublicApiScope, string> = {
  'tasks:read': 'Tarefas: leitura',
  'tasks:write': 'Tarefas: escrita',
  'projects:read': 'Projetos: leitura',
  'projects:write': 'Projetos: escrita',
  'crud:read': 'Clientes/times/usuários: leitura',
  'crud:write': 'Clientes/times/usuários: escrita',
  'time:read': 'Tempo: leitura',
  'time:write': 'Tempo: escrita',
  'time:read_all': 'Tempo: leitura de todos',
  'webhooks:read': 'Webhooks: leitura',
  'webhooks:write': 'Webhooks: escrita',
}

const STATUS_LABEL: Record<PublicApiKey['status'], {text: string; className: string}> = {
  active: {text: 'Ativo', className: 'badge-light-success'},
  expired: {text: 'Expirado', className: 'badge-light-warning'},
  revoked: {text: 'Revogado', className: 'badge-light-danger'},
}

const formatDate = (value: string | null, withTime = true) => {
  if (!value) return null
  try {
    return format(parseISO(value), withTime ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy')
  } catch {
    return null
  }
}

const PublicApiTokenList: FC = () => {
  const [keys, setKeys] = useState<PublicApiKey[]>([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [justCreated, setJustCreated] = useState<{token: string; name: string} | null>(null)

  const swaggerUrl = `${API_CONFIG.BASE_URL}/api/public-api/docs`

  const load = async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const res = await publicApiKeysApi.list()
      setKeys(res.data)
    } catch (e: any) {
      setLoadError(e?.response?.data?.message || 'Erro ao carregar os tokens.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const handleCreated = (token: string, name: string) => {
    setShowCreateModal(false)
    setJustCreated({token, name})
    load()
  }

  const copyToken = async (token: string) => {
    try {
      await navigator.clipboard.writeText(token)
      Toast.fire({icon: 'success', title: 'Token copiado!'})
    } catch {
      Toast.fire({icon: 'error', title: 'Não foi possível copiar automaticamente.'})
    }
  }

  const handleRevoke = (key: PublicApiKey) => {
    const swalWithBootstrapButtons = Swal.mixin({
      customClass: {
        confirmButton: 'btn btn-danger',
        cancelButton: 'btn btn-light',
      },
      buttonsStyling: false,
    })

    swalWithBootstrapButtons
      .fire({
        title: 'Revogar este token?',
        html: `Qualquer aplicação usando <strong>${
          escapeHtml(key.name) || 'este token'
        }</strong> perde o acesso imediatamente. Não pode ser desfeito.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, revogar',
        cancelButtonText: 'Cancelar',
        reverseButtons: true,
      })
      .then(async (result) => {
        if (!result.isConfirmed) return
        try {
          await publicApiKeysApi.revoke(key.id)
          Toast.fire({icon: 'success', title: 'Token revogado.'})
          load()
        } catch (e: any) {
          Toast.fire({
            icon: 'error',
            title: e?.response?.data?.message || 'Não foi possível revogar o token.',
          })
        }
      })
  }

  return (
    <div className='dash-section'>
      <div className='dash-section-head d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4'>
        <div>
          <div className='dash-section-title'>Tokens da API pública</div>
          <div className='text-muted fs-7'>
            Crie um ou mais tokens para consumir a Public API e testar pelo Swagger. Cada token pode
            ter escopos e validade diferentes.
          </div>
        </div>
        <div className='d-flex flex-wrap gap-2'>
          <button className='btn btn-primary' onClick={() => setShowCreateModal(true)}>
            <i className='bi bi-plus-lg me-1'></i>
            Criar token
          </button>
          <a className='btn btn-light-primary' href={swaggerUrl} target='_blank' rel='noreferrer'>
            Abrir Swagger
          </a>
        </div>
      </div>

      {justCreated && (
        <div className='alert alert-success py-3 mb-4'>
          <div className='fw-bold mb-2'>
            Token &quot;{justCreated.name}&quot; criado. Guarde-o agora: ele não será mostrado de
            novo.
          </div>
          <div className='input-group'>
            <input
              className='form-control'
              value={justCreated.token}
              readOnly
              onFocus={(e) => e.currentTarget.select()}
            />
            <button
              className='btn btn-light'
              type='button'
              onClick={() => copyToken(justCreated.token)}
            >
              Copiar
            </button>
            <button
              className='btn btn-light'
              type='button'
              onClick={() => setJustCreated(null)}
              title='Já guardei o token'
            >
              Fechar
            </button>
          </div>
        </div>
      )}

      {loadError && <div className='alert alert-danger py-3 mb-4'>{loadError}</div>}

      {loading ? (
        <div className='text-center py-6'>
          <span className='spinner-border spinner-border-sm me-2' /> Carregando...
        </div>
      ) : keys.length === 0 ? (
        <EmptyState
          icon='bi-key'
          title='Nenhum token criado ainda'
          description='Crie um token para conectar uma extensão, um bot ou uma integração à Public API do TaskRush.'
          actions={[
            {label: 'Criar token', onClick: () => setShowCreateModal(true), variant: 'primary'},
          ]}
        />
      ) : (
        <div className='table-responsive-sm'>
          <table className='table align-middle table-row-dashed fs-6 gy-5'>
            <thead>
              <tr className='text-start text-muted fw-bolder fs-7 border-bottom-0'>
                <th>Nome</th>
                <th>Escopos</th>
                <th>Status</th>
                <th>Último uso</th>
                <th>Expira em</th>
                <th className='text-end'>Ações</th>
              </tr>
            </thead>
            <tbody className='text-gray-600 fw-bold'>
              {keys.map((key) => {
                const status = STATUS_LABEL[key.status]
                return (
                  <tr key={key.id}>
                    <td>
                      {key.name || 'Sem nome'}
                      {key.is_legacy && (
                        <span className='badge badge-light-info ms-2 fs-9'>legado</span>
                      )}
                    </td>
                    <td>
                      <div className='d-flex flex-wrap gap-1'>
                        {key.scopes.length > 0 ? (
                          key.scopes.map((scope) => (
                            <span className='badge badge-light fs-9' key={scope}>
                              {SCOPE_LABELS[scope] || scope}
                            </span>
                          ))
                        ) : (
                          <span className='text-muted fs-8'>Nenhum escopo</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${status.className}`}>{status.text}</span>
                    </td>
                    <td className='fs-7'>{formatDate(key.last_used_at) || 'Nunca usado'}</td>
                    <td className='fs-7'>{formatDate(key.expires_at, false) || 'Sem expiração'}</td>
                    <td className='text-end'>
                      {key.status !== 'revoked' && (
                        <button
                          className='btn btn-sm btn-light-danger'
                          onClick={() => handleRevoke(key)}
                        >
                          Revogar
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <CreateApiTokenModal
        show={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handleCreated}
      />
    </div>
  )
}

export default PublicApiTokenList
